import {
  BottomSheet,
  Button,
  HSpacer,
  IconButton,
  Modal,
  Text,
} from '@/components/DesignSystem';
import { DesktopOnly } from '@/components/shared/DesktopOnly';
import { Box, Stack, SxProps, Theme } from '@mui/material';
import { ReactNode, isValidElement } from 'react';
import { Close } from "@mui/icons-material/";
import { MobileOnly } from '../MobileOnly';
import { ButtonProps } from '@/components/DesignSystem/Button/Button';
import { SXStyles } from '@/themes/variant-interfaces/SXStyles';
import {
  getResponsiveValue,
} from '@/hooks/useMediaQuery';
import { SpacerSize } from '@/themes/variant-interfaces/Size';

interface ModalBottomSheetProps {
  acceptButton?: (props: ButtonProps) => ReactNode,
  centerTitle?: boolean,
  bottomSheetContainerSx?: SxProps<Theme>,
  children: ReactNode,
  closeButton?: ReactNode,
  closeButtonLabel?: string,
  containerSpacing?: number,
  containerStyle?: SxProps<Theme>,
  footer?: ReactNode,
  footerSpacerSize?: SpacerSize,
  headerStyle?: SxProps<Theme>,
  hideBottomCloseButton?: boolean,
  hideScrollbarOnDesktop?: boolean,
  hideTopCloseButton?: boolean,
  secondaryActionButton?: (props: ButtonProps) => ReactNode,
  mobileTitle?: string,
  onClose: () => void,
  onOpen?: () => void,
  open?: boolean,
  showFooter?: boolean,
  showPuller?: boolean,
  showTopButton?: boolean,
  sx?: SxProps<Theme>,
  testID: string,
  title: string | ReactNode,
  titleAccessory?: ReactNode,
  topButtonAction?: ReactNode,
  width?: number,
}

export const ModalBottomSheet = ({
  acceptButton,
  bottomSheetContainerSx,
  centerTitle = false,
  children,
  closeButton,
  closeButtonLabel = "Close",
  containerSpacing,
  containerStyle,
  footer,
  footerSpacerSize = "7",
  headerStyle,
  hideBottomCloseButton,
  hideScrollbarOnDesktop,
  hideTopCloseButton,
  onClose,
  onOpen,
  open = false,
  secondaryActionButton,
  showFooter = true,
  showPuller = false,
  showTopButton = true,
  sx,
  testID,
  title,
  titleAccessory,
  topButtonAction,
  width,
}: ModalBottomSheetProps) => {

  const styles: SXStyles = {
    closeButton: {
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      width: 48,
      height: 48,
    },
  };

  const TopLeftCloseButton = () => (
    <IconButton
      onClick={onClose}
      sx={{
        marginLeft: "-8px",
        marginRight: "-2px",
      }}
      testID={`${testID}-close`}
    >
      <Close sx={{
        width: 24,
        height: 24,
      }} />
    </IconButton>
  );

  const CloseButton = () => (
    <Box
      data-testid={`${testID}-close`}
      onClick={onClose}
      sx={{
        ...styles.closeButton,
        right: getResponsiveValue(10, 13),
        top: getResponsiveValue(10, 13),
      }}
    >
      <Close sx={{
        width: getResponsiveValue(24, 22.6),
        height: getResponsiveValue(24, 22.6),
      }} />
    </Box>
  );

  return (
    <>
      <MobileOnly>
        <BottomSheet
          containerSx={bottomSheetContainerSx}
          footer={footer}
          onClose={onClose}
          onOpen={onOpen ? onOpen : () => null}
          open={open}
          showFooter={showFooter}
          showPuller={showPuller}
          sx={sx}
          testID={`${testID}-mobile-view`}
        >
          <Box sx={{
            padding: showPuller ? "0 0 22px 3px" : "20px 9px 18px 16px",
            ...headerStyle,
          }}>
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
            >
              <Box
                alignItems="center"
                display="flex"
                flexDirection="row"
                width="100%"
              >
                {!!topButtonAction && !hideTopCloseButton && (
                  <>
                    {closeButton && isValidElement(closeButton)
                      ? closeButton
                      : <TopLeftCloseButton />
                    }
                    <HSpacer size='4' />
                  </>
                )}
                {!!titleAccessory && (
                  <>
                    {titleAccessory}
                    <HSpacer size="4" />
                  </>
                )}
                <Stack alignItems={centerTitle ? 'center' : 'start'} width="100%">
                  {typeof title === 'string' ? (
                    <Text
                      breakWord
                      category="title-large"
                      testID={`${testID}-mobile-heading`}
                    >
                      {title}
                    </Text>
                  ) : (
                    <>
                      {title}
                    </>
                  )}
                </Stack>
              </Box>
              {!topButtonAction ? (
                <>
                  {!hideTopCloseButton && <CloseButton />}
                </>
              ) : (
                <Stack direction="row" width="250px">
                  {topButtonAction}
                </Stack>
              )}
            </Stack>
          </Box>
          <Stack
            pb="24px"
            px="16px"
            spacing={containerSpacing}
            sx={{ overflowX: 'hidden', overflowY: 'auto', ...containerStyle }}
          >
            {children}
          </Stack>
        </BottomSheet>
      </MobileOnly>
      <DesktopOnly>
        <Modal
          acceptButton={(props) => acceptButton?.(props)}
          cancelButton={hideBottomCloseButton ? undefined : () => (
            <Button
              onClick={onClose}
              testID={`${testID}-close-button`}
              variant="text"
            >
              {closeButtonLabel}
            </Button>
          )}
          footerSpacerSize={footerSpacerSize}
          headerAccessoryLeft={
            titleAccessory ?? (
              (showTopButton && !!topButtonAction) && (
                <TopLeftCloseButton />
              )
            )}
          headerAccessoryRight={showTopButton && (
            topButtonAction ? topButtonAction : (
              <CloseButton />
            )
          )}
          onClose={onClose}
          open={open}
          padding={3}
          secondaryActionButton={secondaryActionButton}
          testID={`${testID}-desktop-view`}
          title={title}
          titleSpacerSize="6"
          width={width}
        >
          <Stack
            {...(hideScrollbarOnDesktop && { className: 'scrollable-container' })}
            spacing={containerSpacing}
            sx={{
              maxHeight: "490px",
              overflowX: "hidden",
              overflowY: "auto",
              ...containerStyle,
            }}
          >
            {children}
          </Stack>
        </Modal>
      </DesktopOnly>
    </>
  );
};
